export enum SplitLibrarySplitKind {
  EXACT_DELIVERY_DATE_ESTIMATES = "EXACT_DELIVERY_DATE_ESTIMATES",
  FLAT_RATE_VS_REAL_TIME_CARRIER_RATES = "FLAT_RATE_VS_REAL_TIME_CARRIER_RATES",
  PRICE_SENSITIVITY = "PRICE_SENSITIVITY",
  VIP_PROCESSING = "VIP_PROCESSING",
  FREE_SHIPPING_FOR_NEW_CUSTOMERS = "FREE_SHIPPING_FOR_NEW_CUSTOMERS",
  FREE_EXPEDITED_SHIPPING_FOR_HIGH_AOV_CUSTOMERS = "FREE_EXPEDITED_SHIPPING_FOR_HIGH_AOV_CUSTOMERS",
}

export const splitLibrarySplitKindToTitle: Record<
  SplitLibrarySplitKind,
  string
> = {
  [SplitLibrarySplitKind.EXACT_DELIVERY_DATE_ESTIMATES]:
    "Precise delivery date estimates",
  [SplitLibrarySplitKind.FLAT_RATE_VS_REAL_TIME_CARRIER_RATES]:
    "Flat rate vs real-time carrier rates",
  [SplitLibrarySplitKind.PRICE_SENSITIVITY]: "Price sensitivity",
  [SplitLibrarySplitKind.VIP_PROCESSING]: "VIP processing",
  [SplitLibrarySplitKind.FREE_SHIPPING_FOR_NEW_CUSTOMERS]:
    "Free shipping for first-time customers",
  [SplitLibrarySplitKind.FREE_EXPEDITED_SHIPPING_FOR_HIGH_AOV_CUSTOMERS]:
    "Free expedited shipping for high-value customers",
};

/**
 * Short descriptions to show the merchant.
 */
export const splitLibrarySplitKindToDescription: Record<
  SplitLibrarySplitKind,
  string
> = {
  [SplitLibrarySplitKind.EXACT_DELIVERY_DATE_ESTIMATES]:
    "Test displaying precise delivery date estimates to increase conversion and correctly set expectations.",
  [SplitLibrarySplitKind.FLAT_RATE_VS_REAL_TIME_CARRIER_RATES]:
    "Test flat-rate versus real-time carrier shipping rates to improve shipping margins while maintaining conversion.",
  [SplitLibrarySplitKind.PRICE_SENSITIVITY]:
    "Test different shipping fees to find the price that correctly balances order value, shipping margins, and conversion.",
  [SplitLibrarySplitKind.VIP_PROCESSING]:
    "Test offering a $1.99 VIP processing upsell at checkout to increase revenue per order with minimal impact on conversion.",
  [SplitLibrarySplitKind.FREE_SHIPPING_FOR_NEW_CUSTOMERS]:
    "Test offering free shipping to first-time customers to increase conversion and build long-term customer value.",
  [SplitLibrarySplitKind.FREE_EXPEDITED_SHIPPING_FOR_HIGH_AOV_CUSTOMERS]:
    "Test offering discounted expedited shipping on orders over $200 to increase conversion and satisfaction among high-value customers.",
};

/**
 * Lengthy descriptions to give the checkout split agent as instructions on how
 * to create the test.
 */
export const splitLibrarySplitKindToDetailedDescription: Record<
  SplitLibrarySplitKind,
  string
> = {
  [SplitLibrarySplitKind.EXACT_DELIVERY_DATE_ESTIMATES]: `
Test displaying precise delivery date estimates to increase conversion. 

To achieve this, create a split test where the control treatment is the 
existing checkout tree and the variant treatment is the same as the control
treatment (including segmentation), but with the following change:

- Every dynamic shipping method for every checkout should have its description
include "Arrives by {delivery_date}"

Otherwise, all the treatment checkout experiences and segmentation (if any)
should be the same as the control treatment.
`.trim(),
  [SplitLibrarySplitKind.FLAT_RATE_VS_REAL_TIME_CARRIER_RATES]: `
Test flat-rate versus dynamic shipping rates to improve shipping margins while
maintaining conversion. 

To achieve this, create a split test where the control
treatment is the existing checkout tree and the variant treatment is the same
as the control treatment (including segmentation), but with the following 
change:

- If the current checkout tree has checkout experiences with primarily
flat-rate shipping methods, then the variant treatment should have checkout
experiences with dynamic shipping methods.

- If the current checkout tree has checkout experiences with primarily
dynamic shipping methods, then the variant treatment should have checkout
experiences with primarily flat-rate shipping methods.

Otherwise, all the treatment segmentation (if any) should be the same as the
current checkout tree.
`.trim(),
  [SplitLibrarySplitKind.PRICE_SENSITIVITY]: `
Test different shipping fees to find the price that correctly balances order
value, shipping margins, and conversion.

To achieve this, create a split test
where the control treatment is the existing checkout tree and the variant
treatment is the same as the control treatment (including segmentation), but 
with the following change:

- Every shipping method for every checkout experience should have its price
increased by $1 (if it's not a free shipping method). Fixed shipping methods
can be increased by $1. Dynamic shipping methods can be marked up by $1.

Otherwise, all the treatment checkout experiences and segmentation (if any)
should be the same as the control treatment.
`.trim(),
  [SplitLibrarySplitKind.VIP_PROCESSING]: `
Test offering a $1.99 VIP processing upsell at checkout to increase revenue per
order with minimal impact on conversion.

To achieve this, create a split test
where the control treatment is the existing checkout tree and the variant
treatment is the same as the control treatment (including segmentation), but
with the following change:

- For every checkout experience in the current checkout tree, if it primarily
has flat-rate shipping methods, then the analogous variant treatment checkout
experience should include an additional shipping method that takes the shipping
method considered "Standard" and adds an additional $1.99 to the price. It 
should also indicate in the description that the shipping method is more 
expensive than the "Standard" method it was based on because it includes 
"Priority processing".

- For every checkout experience in the current checkout tree, if it primarily
has dynamic shipping methods, then the analogous variant treatment checkout
experience should change the "Standard" shipping method to have an additional
$1.99 markup. It should also indicate in the description that the shipping
method is more expensive because it includes "Priority processing".

Otherwise, all the treatment checkout experiences and segmentation (if any)
should be the same as the control treatment.
`.trim(),
  [SplitLibrarySplitKind.FREE_SHIPPING_FOR_NEW_CUSTOMERS]: `
Test offering free shipping to first-time customers to increase conversion and
build long-term customer value.

To achieve this, create a split test where the control treatment is the
existing checkout tree and the variant treatment is the same as the control
treatment (including segmentation), but with the following change:

- There should be an ORDER_COUNT segment added to the subtree built for the
variant treatment. If the order count is 0, then the associated checkout
experience for that path should include a free shipping option in addition to
the existing corresponding checkout experience's shipping options. If the order
count is greater than 0, then the associated checkout experience for that path
should be the same as the control treatment.

- Note: If the checkout experience for first time customers already has a
shipping method that offers free shipping above a certain order value, then you
should simply remove the condition from that shipping method.

Otherwise, all the treatment checkout experiences and segmentation (if any)
should be the same as the control treatment.
`.trim(),
  [SplitLibrarySplitKind.FREE_EXPEDITED_SHIPPING_FOR_HIGH_AOV_CUSTOMERS]: `
Test offering discounted expedited shipping on orders over $200 to increase
conversion and satisfaction among high-value customers.

To achieve this, create a split test where the control treatment is the
existing checkout tree and the variant treatment is the same as the control
treatment (including segmentation), but with the following change:

- For every checkout experience in the current checkout tree, identify which
shipping method would be considered the "Expedited" shipping method. If one
does not exist, you should add one. Then you should also add a new shipping
method that copies everything about the "Expedited" shipping method except it
makes the price $0 and sets a condition that the order value must be greater
than $200.

Otherwise, all the treatment checkout experiences and segmentation (if any)
should be the same as the control treatment.
`.trim(),
};
